import { MenuItem, Typography, Box, Select } from '@mui/material';
import {
  type MinRetentionRule,
  MinRetentionRuleFrequencyEnum,
  type ControlRules,
} from '@repo/api-gw-sdk';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { Schedule, ScheduleContainer } from '@/components/schedule/schedule';

import { RetentionPopover } from './retentionSelectionPopover';

import { controlsFrequencies, controlsFrequenciesMapping } from '../frequency';

function ScheduleEditor({
  schedule,
  onChange,
  onDelete,
  onDuplicate,
}: {
  schedule: MinRetentionRule;
  onChange: (schedule: MinRetentionRule) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const onFrequencyChange = (frequency: MinRetentionRuleFrequencyEnum) => {
    onChange({
      ...schedule,
      frequency,
      minimumRetention: Math.max(
        schedule.minimumRetention,
        controlsFrequenciesMapping[frequency].minimumRetentionInDays
      ),
    });
  };

  return (
    <ScheduleContainer>
      <Schedule direction='row' alignItems='center' gap='12px'>
        <Select
          value={schedule.frequency}
          onChange={(e) =>
            onFrequencyChange(e.target.value as MinRetentionRuleFrequencyEnum)
          }
          variant='standard'
        >
          {controlsFrequencies.map((m) => (
            <MenuItem key={m.type} value={m.type}>
              {m.text}
            </MenuItem>
          ))}
        </Select>
        <Typography variant='body1'>are retained for at least</Typography>
        <RetentionPopover
          retentionInDays={schedule.minimumRetention}
          minimumRetentionInDays={
            controlsFrequenciesMapping[schedule.frequency]
              .minimumRetentionInDays
          }
          onUpdate={(retentionInDays: number) => {
            onChange({ ...schedule, minimumRetention: retentionInDays });
          }}
        />
        <OptionMenu
          options={[
            {
              id: 'duplicate-min-retention',
              text: 'Duplicate',
            },
            {
              id: 'delete-min-retention',
              text: 'Delete',
            },
          ]}
          onOptionSelected={(option): void => {
            if (typeof option === 'string') return;
            switch (option.text) {
              case 'Duplicate':
                return onDuplicate();
              case 'Delete':
                return onDelete();
            }
          }}
        />
      </Schedule>
    </ScheduleContainer>
  );
}

export function MinimumRetentionEditor({
  rules,
  onChange,
}: {
  rules: ControlRules;
  onChange: (rules: ControlRules) => void;
}) {
  const schedules = (rules.minimumRetention ?? []).filter((r) => r.enabled);
  const updateSchedule = (schedule: MinRetentionRule, index: number) => {
    const newSchedules = [...schedules];
    newSchedules[index] = schedule;
    onChange({
      ...rules,
      minimumRetention: newSchedules,
    });
  };

  return (
    <Box className='my-[24px] ml-[62px] w-full'>
      {schedules?.map((schedule, i) => (
        <ScheduleEditor
          key={i}
          schedule={schedule}
          onChange={(schedule) => updateSchedule(schedule, i)}
          onDelete={() =>
            onChange({
              ...rules,
              minimumRetention: schedules.filter((s, j) => j !== i),
            })
          }
          onDuplicate={() =>
            onChange({ ...rules, minimumRetention: [...schedules, schedule] })
          }
        />
      ))}
      <Box sx={{ marginTop: '16px' }}>
        <ButtonWithIcon
          icon='material-symbols-add-rounded'
          text='Add Another'
          onClick={() =>
            onChange({
              ...rules,
              minimumRetention: [
                ...schedules,
                {
                  enabled: true,
                  minimumRetention:
                    controlsFrequenciesMapping[
                      MinRetentionRuleFrequencyEnum.Daily
                    ].minimumRetentionInDays,
                  frequency: MinRetentionRuleFrequencyEnum.Daily,
                },
              ],
            })
          }
        />
      </Box>
    </Box>
  );
}
